import React, { useEffect, useState } from 'react';
import Container from '@cloudscape-design/components/container';
import Header from '@cloudscape-design/components/header';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Button from '@cloudscape-design/components/button';
import Input from '@cloudscape-design/components/input';
import FormField from '@cloudscape-design/components/form-field';
import Box from '@cloudscape-design/components/box';
import Alert from '@cloudscape-design/components/alert';
import ColumnLayout from '@cloudscape-design/components/column-layout';
import Table from '@cloudscape-design/components/table';
import TextFilter from '@cloudscape-design/components/text-filter';
import { api } from '../api/client';
import { useApi } from '../hooks/useApi';
import { ResponseViewer } from '../components/ResponseViewer';

interface Resource {
  name: string;
  scopes?: string[] | null;
  resources?: Resource[] | null;
}

function flattenResource(r: Resource, prefix = ''): string {
  let result = prefix + r.name;
  if (r.scopes?.length) result += ` (${r.scopes.join(', ')})`;
  if (r.resources?.length) {
    result += ' → ' + r.resources.map(c => flattenResource(c)).join(', ');
  }
  return result;
}

export const ResourcesPage: React.FC<{ app: string }> = ({ app }) => {
  const [resources, setResources] = useState<Resource[]>([]);
  const [name, setName] = useState('');
  const [scopes, setScopes] = useState('');
  const [nestedName, setNestedName] = useState('');
  const [nestedScopes, setNestedScopes] = useState('');
  const [filterText, setFilterText] = useState('');
  const { loading, result, execute } = useApi();

  const load = () => {
    if (!app) return;
    api('GET', `/${encodeURIComponent(app)}/resources`).then(d => { if (Array.isArray(d)) setResources(d); }).catch(() => {});
  };
  useEffect(load, [app]);

  if (!app) return <Alert type="info">Select an application from the top bar to manage resources.</Alert>;

  const create = () => execute(async () => {
    const body: Resource = { name };
    if (scopes) body.scopes = scopes.split(',').map(s => s.trim());
    if (nestedName) {
      const nested: Resource = { name: nestedName };
      if (nestedScopes) nested.scopes = nestedScopes.split(',').map(s => s.trim());
      body.resources = [nested];
    }
    const r = await api('POST', `/${encodeURIComponent(app)}/resource`, body);
    setName(''); setScopes(''); setNestedName(''); setNestedScopes('');
    load();
    return r;
  });

  const remove = (resourceName: string) => execute(async () => {
    const r = await api('DELETE', `/${encodeURIComponent(app)}/resource`, { name: resourceName });
    load();
    return r;
  });

  const filtered = resources.filter(r =>
    !filterText || flattenResource(r).toLowerCase().includes(filterText.toLowerCase())
  );

  return (
    <SpaceBetween size="l">
      <Header variant="h1">Resources</Header>

      <Container header={<Header variant="h2">Create Resource</Header>}>
        <SpaceBetween size="s">
          <ColumnLayout columns={2}>
            <FormField label="Name *">
              <Input value={name} onChange={({ detail }) => setName(detail.value)} placeholder="e.g. country" />
            </FormField>
            <FormField label="Scopes (comma-separated)">
              <Input value={scopes} onChange={({ detail }) => setScopes(detail.value)} placeholder="e.g. US, UK" />
            </FormField>
            <FormField label="Nested Resource Name">
              <Input value={nestedName} onChange={({ detail }) => setNestedName(detail.value)} placeholder="optional nested resource" />
            </FormField>
            <FormField label="Nested Scopes">
              <Input value={nestedScopes} onChange={({ detail }) => setNestedScopes(detail.value)} placeholder="e.g. mustang, raspite" />
            </FormField>
          </ColumnLayout>
          <Button variant="primary" onClick={create} loading={loading} disabled={!name}>
            Create
          </Button>
        </SpaceBetween>
      </Container>

      <Table
        header={<Header counter={`(${filtered.length})`}>Resources in {app}</Header>}
        filter={
          <TextFilter
            filteringText={filterText}
            onChange={({ detail }) => setFilterText(detail.filteringText)}
            filteringPlaceholder="Find resources"
          />
        }
        columnDefinitions={[
          {
            id: 'name',
            header: 'Name',
            cell: item => item.name,
            sortingField: 'name',
          },
          {
            id: 'scopes',
            header: 'Scopes',
            cell: item => item.scopes?.join(', ') || '—',
          },
          {
            id: 'children',
            header: 'Nested Resources',
            cell: item => item.resources?.map(c => flattenResource(c)).join('; ') || '—',
          },
          {
            id: 'actions',
            header: 'Actions',
            width: 120,
            cell: item => <Button variant="inline-link" onClick={() => remove(item.name)}>Delete</Button>,
          },
        ]}
        items={filtered}
        sortingDisabled={false}
        empty={<Box textAlign="center" padding="l">No resources found</Box>}
      />

      <ResponseViewer data={result?.data} error={result?.error} />
    </SpaceBetween>
  );
};
